import { ReactNode, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Heading } from '../Heading';
import { Close } from '../Icons/Close';

export const Dialog = ({
    open,
    onClose,
    title,
    children,
    widthClassName = 'max-w-lg'
}: {
    children?: ReactNode;
    open: boolean;
    onClose?: () => void;
    title?: string;
    widthClassName?: string;
}) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDialogElement>(null);
    const triggerRef = useRef<Element | null>(null);
    const titleId = useId();
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;
        if (open) {
            triggerRef.current = document.activeElement;
            dialog.showModal();
        } else if (dialog.open) {
            dialog.close();
            if (triggerRef.current instanceof HTMLElement) {
                triggerRef.current.focus();
            }
        }
    }, [open]);

    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault(); // intercept before the browser generates a cancel event
            onCloseRef.current?.();
        };
        dialog.addEventListener('keydown', handleKeyDown);
        return () => dialog.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <dialog
            ref={ref}
            aria-labelledby={title ? titleId : undefined}
            onCancel={(e) => {
                e.preventDefault();
                onCloseRef.current?.();
            }}
            className={`backdrop:bg-black/50 w-full ${widthClassName} bg-white rounded-lg m-auto p-8 overflow-y-auto`}
        >
            {title && (
                <Heading variant="h2" id={titleId} classNames="mb-4 lg:mb-6">
                    {title}
                </Heading>
            )}
            {children}
            {onClose && (
                <span className="absolute top-4 right-4">
                    <Button iconbtn onClick={onClose}>
                        <span className="sr-only">{t('Generic.Dialog.Button.Close')}</span>
                        <Close classNames="size-6 stroke-current" />
                    </Button>
                </span>
            )}
        </dialog>
    );
};
